import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Icon,
  ThemeProvider,
  createTheme,
  CssBaseline,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  BroadcastOnHome,
  QuestionAnswer,
  Videocam,
  Event,
  CheckCircle,
  PersonAdd,
} from '@mui/icons-material';


const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#121212',
      paper: '#333333',
    },
    text: {
      primary: '#ffffff',
      secondary: '#e0e0e0',
    },
    primary: { main: '#1976d2' },
    secondary: { main: '#ffb74d' },
  },
  typography: {
    h6: { fontWeight: 'bold' },
    body2: { fontSize: '0.875rem' },
  },
});

// Feature set
const features = [
  { title: 'Create Host Sessions', icon: <BroadcastOnHome />, description: 'Host your own sessions seamlessly.' },
  { title: 'Create Request Sessions', icon: <QuestionAnswer />, description: 'Request sessions easily.' },
  { title: 'Request Session from User', icon: <Videocam />, description: 'Ask users to start sessions.' },
  { title: 'Book Sessions', icon: <Event />, description: 'Book and schedule sessions quickly.' },
  { title: 'Accept Request Sessions', icon: <CheckCircle />, description: 'Accept and manage requests.' },
  { title: 'Get Requests from User', icon: <PersonAdd />, description: 'Users can request to join you.' },
];

const borderColors = ['#1976d2', '#ffb74d', '#4caf50', '#e91e63', '#f44336', '#9c27b0'];

const VerifiedUsersDiagram = () => {
  const [hoveredCard, setHoveredCard] = useState(null);

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));

  const radius = isXs ? 108 : isSm ? 200 : isMd ? 280 : 340;
  const cardSize = isXs ? 90 : isSm ? 110 : isMd ? 130 : 160;
  const iconFontSize = isXs ? 28 : isSm ? 34 : isMd ? 40 : 48;
  const titleFontSize = isXs ? '0.7rem' : isSm ? '0.8rem' : isMd ? '0.9rem' : '1rem';
  const descFontSize = isXs ? '0.6rem' : isSm ? '0.7rem' : isMd ? '0.75rem' : '0.8rem';

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: {
            xs: '50vh',   // extra-small screens (mobile)
            sm: '70vh',   // small screens (tablets)
            md: '100vh',   // medium screens (small laptops)
            lg: '130vh',   // large screens (desktops)
            xl: '150vh',   // extra-large screens
          },
          backgroundColor: '#000',
          p: 2,
          position: 'relative',
        }}
      >
        {/* Title */}
        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontWeight: 600,
            textAlign: 'center',
            fontSize: {
              xs: '1.5rem',
              sm: '2rem',
              md: '2.5rem',
              lg: '3rem',
            },
            backgroundImage: 'linear-gradient(45deg, #00bfff, #ff00ff)',
            backgroundClip: 'text',
            color: 'transparent',
            letterSpacing: 7,
            // mb: 4,
          }}
        >
          User
        </Typography>

        {/* Cards in circle */}
        {features.map((feature, index) => {
          const angle = (index / features.length) * 360;
          const x = radius * Math.cos((angle * Math.PI) / 180);
          const y = radius * Math.sin((angle * Math.PI) / 180);

          return (
            <Box
              key={index}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: `translate(-50%, -50%) translate(${x}px, ${y}px)`,
              }}
            >
              <Card
                onMouseEnter={() => setHoveredCard(index)}
                onMouseLeave={() => setHoveredCard(null)}
                sx={{
                  clipPath: 'polygon(50% 0%, 100% 35%, 82% 100%, 18% 100%, 0% 35%)',
                  backgroundColor: '#000',
                  width: cardSize,
                  height: cardSize,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  border: `2px solid ${borderColors[index % borderColors.length]}`,
                  transition: 'all 0.3s ease',
                  boxShadow: 3,
                  '&:hover': {
                    transform: 'scale(1.7)',
                    zIndex: 10,
                    borderColor: '#fff',
                  },
                }}
              >
                <CardContent
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 1,
                  }}
                >
                  <Icon sx={{ fontSize: iconFontSize, color: '#1976d2' }}>
                    {feature.icon}
                  </Icon>

                  <Typography
                    sx={{
                      mt: 1,
                      fontSize: titleFontSize,
                      fontWeight: 600,
                      color: '#1E90FF',
                      letterSpacing: 1,
                    }}
                  >
                    {feature.title}
                  </Typography>

                  {hoveredCard === index && (
                    <Typography
                      sx={{
                        mt: 0.5,
                        fontSize: descFontSize,
                        color: '#ccc',
                        textAlign: 'center',
                        letterSpacing: 1,
                        maxWidth: '90%',
                      }}
                    >
                      {feature.description}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Box>
          );
        })}
      </Box>
    </ThemeProvider>
  );
};

export default VerifiedUsersDiagram;
